import type { MobileCheckInResponse } from '@platform/shared-types';
import { KEYS, readJson, safeGet, safeSet, safeRemove } from './storage';
import type { KeyValueStore } from './storage';

/** Opaque handle the updater returns for a downloaded bundle. */
export interface DownloadedBundle { id: string }

/**
 * Over-the-air updater port. P3 adapter = Capgo capacitor-updater:
 * download→download({checksum}), applyNow→set (reloads), applyOnNextStart→next,
 * resetToBuiltin→reset, notifyReady→notifyAppReady (Capgo auto-reverts a
 * bundle that never calls it).
 */
export interface UpdaterPort {
  currentBundleId(): Promise<string | null>;
  notifyReady(): Promise<void>;
  download(o: { bundleId: string; url: string; sha256: string }): Promise<DownloadedBundle>;
  applyNow(b: DownloadedBundle): Promise<void>;
  applyOnNextStart(b: DownloadedBundle): Promise<void>;
  resetToBuiltin(): Promise<void>;
}

export type BundleResult = 'none' | 'applied-now' | 'scheduled' | 'reset' | 'failed';

const report = (store: KeyValueStore, bundleId: string, outcome: 'ok' | 'reverted') =>
  safeSet(store, KEYS.lastApply, JSON.stringify({ bundleId, outcome }));

/** What we switched to, and on which native build — a store update in between is not a failed start. */
interface Pending { bundleId: string; nativeBuild: number | null }
const isPending = (v: unknown): v is Pending =>
  typeof v === 'object' && v !== null
  && typeof (v as any).bundleId === 'string' && (v as any).bundleId.length > 0 && (v as any).bundleId.length <= 64
  && ((v as any).nativeBuild === null || typeof (v as any).nativeBuild === 'number');

const isDownloaded = (v: unknown): v is { bundleId: string; id: string } =>
  typeof v === 'object' && v !== null && typeof (v as any).bundleId === 'string' && typeof (v as any).id === 'string';

/** Read the pending marker; a corrupt/foreign value is removed, never reported. */
async function readPending(store: KeyValueStore): Promise<Pending | null> {
  const pending = await readJson(store, KEYS.pendingBundle, isPending);
  if (!pending && (await safeGet(store, KEYS.pendingBundle)) !== null) await safeRemove(store, KEYS.pendingBundle);
  return pending;
}

async function forgetDownloadOf(store: KeyValueStore, bundleId: string): Promise<void> {
  const d = await readJson(store, KEYS.downloadedBundle, isDownloaded);
  if (d && d.bundleId === bundleId) await safeRemove(store, KEYS.downloadedBundle);
}

/**
 * Cold start: if we switched to a bundle and it is NOT what's running, the
 * updater reverted it (it never reached markHealthy) — report it. Unless the
 * native build changed: a store update drops downloaded bundles, which is not
 * a failed start and must not pause the rollout for everyone.
 */
export async function recordBootOutcome(store: KeyValueStore, updater: UpdaterPort, nativeBuild: number | null): Promise<void> {
  const pending = await readPending(store);
  if (!pending) return;
  let current: string | null = null;
  try { current = await updater.currentBundleId(); } catch { return; }
  if (current === pending.bundleId) return; // running — markHealthy will confirm it
  await safeRemove(store, KEYS.pendingBundle);
  await forgetDownloadOf(store, pending.bundleId); // the updater deleted it; a re-offer must download fresh
  if (pending.nativeBuild !== null && nativeBuild !== null && pending.nativeBuild !== nativeBuild) return;
  await report(store, pending.bundleId, 'reverted');
}

/**
 * Call once the app has rendered. Tells the updater this bundle works (so it
 * is kept) and reports the good start of a newly applied bundle.
 */
export async function markHealthy(store: KeyValueStore, updater: UpdaterPort): Promise<void> {
  try { await updater.notifyReady(); } catch { return; }
  const pending = await readPending(store);
  if (!pending) return;
  let current: string | null = null;
  try { current = await updater.currentBundleId(); } catch { return; }
  if (current !== pending.bundleId) return;
  await report(store, pending.bundleId, 'ok');
  await safeRemove(store, KEYS.pendingBundle);
}

/**
 * Bring the phone to the offered bundle. `applyNow()` is asked AFTER the
 * download, so a slow download can fall back to next start (startup.ts).
 */
export async function syncBundle(
  ports: { store: KeyValueStore; updater: UpdaterPort; nativeBuild: number | null },
  response: MobileCheckInResponse,
  applyNow: () => boolean,
): Promise<BundleResult> {
  const { store, updater } = ports;
  try {
    if (response.resetToBuiltin) {
      await safeRemove(store, KEYS.pendingBundle);
      await safeRemove(store, KEYS.downloadedBundle);
      await updater.resetToBuiltin();
      return 'reset';
    }
    const offer = response.bundle;
    if (!offer) return 'none';
    if ((await updater.currentBundleId()) === offer.bundleId) return 'none';

    const apply = async (handle: DownloadedBundle): Promise<BundleResult> => {
      await safeSet(store, KEYS.pendingBundle, JSON.stringify({ bundleId: offer.bundleId, nativeBuild: ports.nativeBuild }));
      if (applyNow()) {
        await updater.applyNow(handle);
        return 'applied-now';
      }
      await updater.applyOnNextStart(handle);
      return 'scheduled';
    };

    const prior = await readJson(store, KEYS.downloadedBundle, isDownloaded);
    if (prior && prior.bundleId === offer.bundleId) {
      try {
        return await apply({ id: prior.id });
      } catch {
        // The updater may have deleted it (revert, cleanup). Never retry a dead
        // handle forever — drop it and download fresh below.
        await safeRemove(store, KEYS.downloadedBundle);
      }
    }
    const handle = await updater.download(offer);
    await safeSet(store, KEYS.downloadedBundle, JSON.stringify({ bundleId: offer.bundleId, id: handle.id }));
    return await apply(handle);
  } catch {
    await safeRemove(store, KEYS.pendingBundle);
    await safeRemove(store, KEYS.downloadedBundle);
    return 'failed';
  }
}
